import React from "react";
import {
  CustomerUpdate,
  CustomerUpdateStatus,
  CustomerUpdateStatusLabels,
  Employee,
  Snooze,
} from "@/types/v2";
import { cn, firestoreToDateTime } from "@/lib/utils";
import { format } from "date-fns";
import { Separator } from "@/components/Base/Separator";
import { getEmployeeName } from "@/lib/employees";
import { statusClasses } from "@/lib/customerUpdates";

export type CustomerUpdateTimelineEntryProps = {
  update: CustomerUpdate;
  employees: Employee[];
  dense?: boolean;
  isOpenDefault?: boolean;
  onSelectUpdate?: (u: CustomerUpdate) => void;
  showCardInfo?: boolean;
  showMinimalTimestamps?: boolean;
  collapsedNotes?: boolean;
};

/**
 *
 * @param dt
 */
function formatDate(dt?: Date): string {
  if (!dt) return "";
  try {
    return format(dt, "MMM d, yyyy h:mm a");
  } catch {
    return dt.toString();
  }
}

/**
 *
 * @param u
 */
function getPrimaryTimestamp(u: CustomerUpdate): Date | undefined {
  // Choose the most relevant timestamp based on status
  switch (u.status) {
    case CustomerUpdateStatus.COMPLETED:
      return firestoreToDateTime(u.completedAt);
    case CustomerUpdateStatus.SKIPPED:
      return firestoreToDateTime(u.skippedAt);
    case CustomerUpdateStatus.CANCELLED:
      return firestoreToDateTime(u.cancelledAt);
    case CustomerUpdateStatus.SNOOZED:
      // Show last snooze time if available else scheduled/pending/created
      if (u.snoozes && u.snoozes.length > 0) {
        const last = u.snoozes[u.snoozes.length - 1];
        return firestoreToDateTime(last.snoozedAt);
      }
      return (
        firestoreToDateTime(u.pendingAt) ||
        firestoreToDateTime(u.scheduledAt) ||
        firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.PENDING:
      return (
        firestoreToDateTime(u.pendingAt) ||
        firestoreToDateTime(u.scheduledAt) ||
        firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.SCHEDULED:
      return (
        firestoreToDateTime(u.scheduledAt) || firestoreToDateTime(u.createdAt)
      );
    case CustomerUpdateStatus.CREATED:
    default:
      return firestoreToDateTime(u.createdAt);
  }
}

/**
 *
 * @param u
 * @param employees
 */
function getActor(
  u: CustomerUpdate,
  employees: Employee[]
): string | undefined {
  let actor;
  switch (u.status) {
    case CustomerUpdateStatus.COMPLETED:
      actor = u.completedBy;
      break;
    case CustomerUpdateStatus.SKIPPED:
      actor = u.skippedBy;
      break;
    case CustomerUpdateStatus.CANCELLED:
      actor = u.cancelledBy;
      break;
    case CustomerUpdateStatus.SNOOZED:
      break;
    case CustomerUpdateStatus.PENDING:
      actor = u.pendingBy;
      break;
    case CustomerUpdateStatus.SCHEDULED:
      actor = u.scheduledBy;
      break;
    case CustomerUpdateStatus.CREATED:
      actor = u.createdBy;
      break;
    default:
      actor = u.updatedBy ?? u.createdBy;
      break;
  }
  return getEmployeeName(actor, employees, true);
}

/**
 *
 * @param root0
 * @param root0.label
 * @param root0.value
 */
function DetailsRow({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3 py-1 text-sm">
      <div className="w-32 shrink-0 text-muted-foreground">{label}</div>
      <div className="flex-1 break-words">{value}</div>
    </div>
  );
}

/**
 *
 * @param root0
 * @param root0.update
 * @param root0.employees
 * @param root0.dense
 * @param root0.isOpenDefault
 * @param root0.onSelectUpdate
 * @param root0.showCardInfo
 * @param root0.showMinimalTimestamps
 */
export function CustomerUpdateTimelineEntry({
  update: u,
  employees,
  dense = true,
  isOpenDefault = false,
  onSelectUpdate,
  showCardInfo = true,
  showMinimalTimestamps = false,
  collapsedNotes = false,
}: CustomerUpdateTimelineEntryProps) {
  const primaryTime = getPrimaryTimestamp(u);
  const primaryTimeText = formatDate(primaryTime);
  const actor = getActor(u, employees);

  // Determine mutually exclusive detail message
  let statusDetail: React.ReactNode = null;
  if (u.status === CustomerUpdateStatus.COMPLETED) {
    statusDetail = <DetailsRow label="Completed At" value={primaryTimeText} />;
  } else if (u.status === CustomerUpdateStatus.SKIPPED) {
    statusDetail = (
      <>
        <DetailsRow label="Skipped At" value={primaryTimeText} />
        <DetailsRow label="Reason" value={u.skippedReason} />
      </>
    );
  } else if (u.status === CustomerUpdateStatus.CANCELLED) {
    statusDetail = (
      <>
        <DetailsRow label="Cancelled At" value={primaryTimeText} />
        <DetailsRow label="Reason" value={u.cancelledReason} />
      </>
    );
  }

  // other timestamps for deeper dive
  const scheduled = firestoreToDateTime(u.scheduledUpdateTime);
  const pending = firestoreToDateTime(u.pendingAt);
  const created = firestoreToDateTime(u.createdAt);
  const updated = firestoreToDateTime(u.updatedAt);

  const comments =
    u.status === CustomerUpdateStatus.CANCELLED
      ? u.cancelledReason
      : u.status === CustomerUpdateStatus.SKIPPED
        ? u.skippedReason
        : u.comments;

  return (
    <li className="rounded-md border">
      <details open={isOpenDefault} className="group">
        <summary
          className={cn(
            "flex cursor-pointer list-none items-center gap-3 p-3",
            dense ? "py-2" : "py-3"
          )}
          onClick={() => {
            // Allow onSelectUpdate without preventing toggle
            onSelectUpdate?.(u);
          }}
        >
          <span
            className={cn(
              "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium",
              statusClasses[u.status]
            )}
          >
            {CustomerUpdateStatusLabels[u.status]}
          </span>
          {collapsedNotes && actor && (
            <span className="truncate text-xs text-muted-foreground">
              {actor.split(" ")[0]}:
            </span>
          )}
          <span className="text-sm text-muted-foreground">
            {collapsedNotes ? comments : primaryTimeText}
          </span>
          {actor && !collapsedNotes && (
            <span className="truncate text-xs text-muted-foreground">
              by {actor}
            </span>
          )}
          {showCardInfo && (
            <span className="ml-auto text-xs text-muted-foreground">
              {u.cardNumber ? `RO ${u.cardNumber}` : u.cardId}
            </span>
          )}
        </summary>

        <Separator />

        <div className={cn("space-y-2 p-3", dense ? "py-2" : "py-3")}>
          {comments && (
            <DetailsRow
              label="Notes"
              value={<span className="whitespace-pre-wrap">{comments}</span>}
            />
          )}

          {statusDetail}

          {/* Snooze History */}
          {u.snoozes && u.snoozes.length > 0 && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                Snooze History ({u.snoozes.length})
              </div>
              <div className="rounded-md border">
                {u.snoozes.map((s: Snooze, i: number) => (
                  <div
                    key={i}
                    className={cn(
                      "grid grid-cols-1 gap-2 p-2 text-xs md:grid-cols-4",
                      i !== (u.snoozes?.length ?? 0) - 1 && "border-b"
                    )}
                  >
                    <div>
                      <div className="text-muted-foreground">Snoozed</div>
                      <div>{formatDate(firestoreToDateTime(s.snoozedAt))}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">By</div>
                      <div>{getEmployeeName(s.snoozedBy, employees, true)}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Scheduled</div>
                      <div>
                        {formatDate(
                          firestoreToDateTime(s.originalScheduledUpdateTime)
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Secondary Timestamps */}
          {!showMinimalTimestamps && (
            <div className="mt-1 grid grid-cols-1 gap-1 md:grid-cols-2">
              <DetailsRow label="Scheduled At" value={formatDate(scheduled)} />
              <DetailsRow label="Pending At" value={formatDate(pending)} />
              <DetailsRow label="Created" value={formatDate(created)} />
              <DetailsRow label="Updated" value={formatDate(updated)} />
            </div>
          )}
        </div>
      </details>
    </li>
  );
}

export default CustomerUpdateTimelineEntry;
